import React from 'react';
import { MessageCircle, Repeat2, Heart, Share2, BarChart2, MoreHorizontal } from 'lucide-react';
import { MediaDisplay } from './MediaDisplay';
import type { PreviewProps } from './types';

export const TwitterPreview: React.FC<PreviewProps> = ({ 
  content, 
  brand, 
  brandName,
  brandLogo,
  mediaUrl, 
  format = 'regular' 
}) => {
  // Get the Twitter account from brand's social accounts if available
  const getTwitterAccount = () => {
    if (brand?.social_accounts && Array.isArray(brand.social_accounts)) {
      return brand.social_accounts.find(
        account => account.platform.toLowerCase() === 'twitter'
      );
    }
    return null;
  };
  
  const twitterAccount = getTwitterAccount();
  
  // Get the Twitter username
  const getTwitterUsername = (): string => {
    if (twitterAccount?.username) {
      return twitterAccount.username;
    }
    
    // Fallback to the brandName or brand.name if no Twitter account found
    const name = brandName || brand?.name || 'brandname';
    return name.toLowerCase().replace(/\s/g, '');
  };
  
  // Get the profile picture
  const getProfilePicture = (): string => {
    if (twitterAccount?.picture_url) {
      return twitterAccount.picture_url;
    }
    
    // Fallback to brandLogo or the old behavior
    if (brandLogo) {
      return brandLogo;
    }
    
    // Final fallback to default avatar
    return 'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png';
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden w-full">
      <div className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
            {getProfilePicture() ? (
              <img src={getProfilePicture()} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gray-200" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1 text-sm sm:text-base">
                  <span className="font-bold text-gray-900 truncate">
                    {brandName || brand?.name || 'Brand Name'}
                  </span>
                  <span className="text-gray-500 truncate">@{getTwitterUsername()}</span>
                  <span className="text-gray-500 mx-1">·</span>
                  <span className="text-gray-500">2h</span>
                </div>
              </div>
              <button className="flex-shrink-0">
                <MoreHorizontal className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>
            
            {/* Content */}
            <div className="mt-1">
              <p className="text-gray-900 whitespace-pre-line" style={{ fontSize: '0.8rem' }}>
                {content}
              </p>
              
              {/* Media */}
              {mediaUrl && (
                <div className={`mt-3 bg-gray-100 rounded-xl overflow-hidden ${
                  format === 'reel' ? 'aspect-[9/16]' : 'aspect-video'
                }`}>
                  <MediaDisplay
                    mediaUrl={mediaUrl}
                    altText="Twitter media"
                    type={format === 'reel' ? 'video' : 'image'}
                    className="w-full h-full"
                  />
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-between mt-3 text-gray-500">
              <button className="flex items-center group">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center group-hover:bg-blue-50 group-hover:text-blue-500 rounded-full">
                  <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <span className="text-xs sm:text-sm ml-1">24</span>
              </button>
              
              <button className="flex items-center group">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center group-hover:bg-green-50 group-hover:text-green-500 rounded-full">
                  <Repeat2 className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <span className="text-xs sm:text-sm ml-1">142</span>
              </button>
              
              <button className="flex items-center group">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center group-hover:bg-red-50 group-hover:text-red-500 rounded-full">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <span className="text-xs sm:text-sm ml-1">1.2K</span>
              </button>
              
              <button className="flex items-center group">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center group-hover:bg-blue-50 group-hover:text-blue-500 rounded-full">
                  <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <span className="text-xs sm:text-sm ml-1">247K</span>
              </button>
              
              <button className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-blue-50 hover:text-blue-500 rounded-full">
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};